import { cloneDeep } from "lodash-es"

import commonApi from "@/api/common"
import type {
  TrackingPageThemeImageApiValue,
  TrackingPageThemeLayoutInlinePayload,
} from "@/models/ITrackingPageThemeLayoutConfig"
import {
  emptyTrackingPageThemeMediaImage,
  TrackingPageThemeContentColumnType,
} from "@/models/ITrackingPageThemeLayoutConfig"
import { normalizeThemeImageFromApi } from "@/utils/themeLayoutThemeImageField"

const SHOPIFY_MEDIA_IMAGE_GID_PREFIX = "gid://shopify/MediaImage/"

const isShopifyMediaImageGid = (gid: string): boolean =>
  gid.startsWith(SHOPIFY_MEDIA_IMAGE_GID_PREFIX)

const collectShopifyMediaImageGids = (payload: TrackingPageThemeLayoutInlinePayload): string[] => {
  const gids = new Set<string>()

  const track = (image: TrackingPageThemeImageApiValue | undefined): void => {
    const row = normalizeThemeImageFromApi(image)

    if (row?.gid && isShopifyMediaImageGid(row.gid)) {
      gids.add(row.gid)
    }
  }

  if ("columns" in payload.content) {
    for (const column of payload.content.columns) {
      if (column.type === TrackingPageThemeContentColumnType.Image) {
        track(column.image)
      }
    }
  } else {
    track(payload.content.top_section.image_banner.image)
  }

  for (const item of payload.content.bottom_section.images) {
    track(item.image)
  }

  return [...gids]
}

interface ThemeImageSyncChange {
  removedGids: string[]
  /** url 发生变化（gid 存在但 CDN 链接更新，常见为 `?v=` 版本号变动）的项 */
  updatedGids: string[]
}

/**
 * 按 sync 结果就地处理单个图片字段：
 * - gid 已删除 → 置空
 * - gid 仍存在且 url 变化 → 更新为最新 url
 * 返回应写回的新图片值，并把命中的 gid 记入 change。
 */
const syncImageValue = (
  value: TrackingPageThemeImageApiValue | undefined,
  deletedGids: Set<string>,
  latestUrlByGid: Map<string, string>,
  change: ThemeImageSyncChange,
): TrackingPageThemeImageApiValue | undefined => {
  const row = normalizeThemeImageFromApi(value)
  const gid = row?.gid

  if (!gid || !isShopifyMediaImageGid(gid)) {
    return value
  }

  if (deletedGids.has(gid)) {
    change.removedGids.push(gid)

    return emptyTrackingPageThemeMediaImage()
  }

  const latestUrl = latestUrlByGid.get(gid)

  if (row && latestUrl && latestUrl !== row.url) {
    // eslint-disable-next-line no-console
    console.log("[theme-image-sync] url 变更，同步到 store", {
      gid,
      from: row.url,
      to: latestUrl,
    })
    change.updatedGids.push(gid)

    return { ...row, url: latestUrl }
  }

  return value
}

const syncImagesInPayload = (
  payload: TrackingPageThemeLayoutInlinePayload,
  deletedGids: Set<string>,
  latestUrlByGid: Map<string, string>,
): ThemeImageSyncChange => {
  const change: ThemeImageSyncChange = { removedGids: [], updatedGids: [] }

  if ("columns" in payload.content) {
    for (const column of payload.content.columns) {
      if (column.type === TrackingPageThemeContentColumnType.Image) {
        column.image = syncImageValue(
          column.image as TrackingPageThemeImageApiValue,
          deletedGids,
          latestUrlByGid,
          change,
        )
      }
    }
  } else {
    payload.content.top_section.image_banner.image = syncImageValue(
      payload.content.top_section.image_banner.image as TrackingPageThemeImageApiValue,
      deletedGids,
      latestUrlByGid,
      change,
    )
  }

  for (const item of payload.content.bottom_section.images) {
    item.image = syncImageValue(
      item.image as TrackingPageThemeImageApiValue,
      deletedGids,
      latestUrlByGid,
      change,
    )
  }

  return change
}

export interface RemoveShopifyMediaImageGidsResult {
  payload: TrackingPageThemeLayoutInlinePayload
  modified: boolean
  removedGids: string[]
}

/**
 * 就地移除配置中引用了指定 gid（已知被删除）的图片，置空对应图片字段。
 * 与 {@link pruneMissingShopifyMediaImagesInThemePayload} 不同：不调用 /sync 接口，
 * 适用于「在应用内刚删除某张图片、gid 已知」的场景，直接据此清理引用。
 * 不修改入参，命中时返回全新 payload（深拷贝）。
 */
export const removeShopifyMediaImageGidsFromPayload = (
  payload: TrackingPageThemeLayoutInlinePayload,
  gids: string[],
): RemoveShopifyMediaImageGidsResult => {
  const deletedGids = new Set(gids.filter((gid) => gid && isShopifyMediaImageGid(gid)))

  if (deletedGids.size === 0) {
    return { payload, modified: false, removedGids: [] }
  }

  const nextPayload = cloneDeep(payload)
  // 复用同步逻辑：deletedGids 命中即置空；latestUrlByGid 留空，故不会触发任何 url 更新
  const change = syncImagesInPayload(nextPayload, deletedGids, new Map())
  const modified = change.removedGids.length > 0

  return {
    payload: modified ? nextPayload : payload,
    modified,
    removedGids: change.removedGids,
  }
}

export interface PruneThemeLayoutMissingShopifyMediaImagesResult {
  payload: TrackingPageThemeLayoutInlinePayload
  modified: boolean
  removedGids: string[]
  /** url 被刷新的 gid（CDN 链接变动） */
  updatedGids: string[]
}

/**
 * 遍历主题配置中的 Shopify MediaImage，通过 /v4/common/images/sync 同步：
 * - deleted_gids：服务端已删除（并同步删除 common images 记录），据此移除配置中的引用；
 * - items：仍存在的图片，若其 url（CDN 链接，含 `?v=` 版本号）变化则刷新到配置。
 */
export const pruneMissingShopifyMediaImagesInThemePayload = async (
  payload: TrackingPageThemeLayoutInlinePayload,
): Promise<PruneThemeLayoutMissingShopifyMediaImagesResult> => {
  const gids = collectShopifyMediaImageGids(payload)

  if (gids.length === 0) {
    return { payload, modified: false, removedGids: [], updatedGids: [] }
  }

  const { data } = await commonApi.syncCommonImages(gids)
  const deletedGids = new Set(data.images?.deleted_gids ?? [])
  const latestUrlByGid = new Map(
    (data.images?.items ?? [])
      .filter((item) => item.gid && item.url)
      .map((item) => [item.gid, item.url] as const),
  )

  const nextPayload = cloneDeep(payload)
  const change = syncImagesInPayload(nextPayload, deletedGids, latestUrlByGid)
  const modified = change.removedGids.length > 0 || change.updatedGids.length > 0

  if (modified) {
    // eslint-disable-next-line no-console
    console.log("[theme-image-sync] 配置已更新", {
      removedGids: change.removedGids,
      updatedGids: change.updatedGids,
    })
  }

  return {
    payload: modified ? nextPayload : payload,
    modified,
    removedGids: change.removedGids,
    updatedGids: change.updatedGids,
  }
}
